import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import FormField from './FormField';
import { formatDateTime } from './dateUtils';
import fieldConfigService from '../services/fieldConfigService';
import _ from 'lodash';

const WorkItemDetail = ({ workItem, onClose, azureService }) => {
    const [comment, setComment] = useState('');
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const availableColumns = process.env.REACT_APP_AVAILABLE_COLUMNS.split(',');
    const isBug = workItem.fields['System.WorkItemType'] === 'Bug';

    useEffect(() => {
        if (!azureService?.getWorkItemComments) {
            console.error('Azure service or getWorkItemComments method not available');
            setError('Comments service not available');
            setLoading(false);
            return;
        }

        const fetchComments = async () => {
            try {
                setLoading(true);
                setError(null);
                const fetchedComments = await azureService.getWorkItemComments(workItem.id);
                setComments(fetchedComments || []);
            } catch (error) {
                console.error('Error fetching comments:', error);
                setError('Failed to load comments');
                setComments([]);
            } finally {
                setLoading(false);
            }
        };

        fetchComments();
    }, [workItem.id, azureService]);

    const handleSubmitComment = async (e) => {
        e.preventDefault();
        if (!comment.trim() || submitting || !azureService?.addComment) {
            return;
        }

        try {
            setSubmitting(true);
            setError(null);
            await azureService.addComment(workItem.id, comment);

            const fetchedComments = await azureService.getWorkItemComments(workItem.id);
            setComments(fetchedComments || []);
            setComment('');
        } catch (error) {
            console.error('Error adding comment:', error);
            setError('Failed to add comment');
        } finally {
            setSubmitting(false);
        }
    };

    const handleUpdateField = async (field, newValue) => {
        try {
            await azureService.updateWorkItem(workItem.id, [
                { field, value: newValue }
            ]);
            // Update the local state to reflect the change
            workItem.fields[field] = newValue;
        } catch (error) {
            console.error('Error updating field:', error);
            setError('Failed to update field');
            throw error;
        }
    };

    const getFormField = (field) => {
        const value = _.get(workItem.fields, field);
        const config = fieldConfigService.getFieldConfig(field);

        // Make state field editable for bugs, using the config from environment
        if (field === 'System.State' && isBug) {
            const stateConfig = {
                ...config,
                editable: true,
                type: 'select'
            };
            return (
                <FormField
                    fieldKey={field}
                    value={value}
                    onUpdate={handleUpdateField}
                    fieldType={stateConfig.type}
                    isEditable={stateConfig.editable}
                    options={stateConfig.options}
                    placeholder={stateConfig.placeholder}
                />
            );
        }

        return (
            <FormField
                fieldKey={field}
                value={value}
                onUpdate={handleUpdateField}
                fieldType={config.type}
                isEditable={config.editable}
                options={config.options}
                placeholder={config.placeholder}
            />
        );
    };

    const getFieldDisplayName = (field) => {
        return field.split('.').pop().replace(/([A-Z])/g, ' $1').trim();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="p-4 border-b">
                    <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center gap-2">
                            <div className="text-sm text-gray-500">#{workItem.id}</div>
                            <a
                                href={`https://dev.azure.com/${process.env.REACT_APP_AZURE_DEVOPS_ORG}/${process.env.REACT_APP_AZURE_DEVOPS_PROJECT}/_workitems/edit/${workItem.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                            >
                                Open in Azure DevOps
                            </a>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-1 hover:bg-gray-100 rounded"
                        >
                            <X size={20} />
                        </button>
                    </div>
                    <div className="text-xl font-semibold">
                        <textarea
                            value={workItem.fields['System.Title'] || ''}
                            onChange={(e) => handleUpdateField('System.Title', e.target.value)}
                            className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[60px] resize-y"
                            placeholder="Enter title..."
                        />
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto">
                    {/* Fields Section */}
                    <div className="p-4 border-b">
                        <div className="grid grid-cols-2 gap-4">
                            {availableColumns.map((field) => (
                                <div key={field} className="space-y-1">
                                    <label className="block text-sm font-medium text-gray-600">
                                        {getFieldDisplayName(field)}
                                    </label>
                                    {getFormField(field)}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Description Section */}
                    {workItem.fields['System.Description'] && (
                        <div className="p-4 border-b">
                            <h3 className="text-lg font-medium mb-2">Description</h3>
                            <div
                                className="prose prose-sm max-w-none"
                                dangerouslySetInnerHTML={{ __html: workItem.fields['System.Description'] }}
                            />
                        </div>
                    )}

                    {/* Comments Section */}
                    <div className="p-4">
                        <h3 className="text-lg font-medium mb-4">Comments</h3>

                        {error && (
                            <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md">
                                {error}
                            </div>
                        )}

                        <div className="space-y-4 mb-4">
                            {loading ? (
                                <div className="text-gray-500">Loading comments...</div>
                            ) : comments && comments.length > 0 ? (
                                comments.map((comment) => (
                                    <div key={comment.id} className="border-b pb-4">
                                        <div className="flex justify-between items-start mb-2">
                                            <div>
                                                <div className="font-medium">
                                                    {comment.modifiedBy?.displayName || 'Unknown User'}
                                                </div>
                                                <div className="text-sm text-gray-500">
                                                    {formatDateTime(comment.createdDate)}
                                                </div>
                                            </div>
                                            {comment.modifiedDate !== comment.createdDate && (
                                                <div className="text-xs text-gray-400">
                                                    Edited {formatDateTime(comment.modifiedDate)}
                                                </div>
                                            )}
                                        </div>
                                        <div
                                            className="prose prose-sm max-w-none"
                                            dangerouslySetInnerHTML={{ __html: comment.text }}
                                        />
                                    </div>
                                ))
                            ) : (
                                <div className="text-gray-500">No comments yet</div>
                            )}
                        </div>

                        {/* Add Comment Form */}
                        <form onSubmit={handleSubmitComment} className="mt-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Add Comment
                            </label>
                            <textarea
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                rows="3"
                                placeholder="Type your comment here..."
                            />
                            <div className="mt-2 flex justify-end">
                                <button
                                    type="submit"
                                    disabled={!comment.trim() || submitting}
                                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {submitting ? 'Adding...' : 'Add Comment'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WorkItemDetail;